import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Field } from 'react-final-form';

import Typography from './Modules/Components/Typography';
import AppFooter from './Modules/Views/AppFooter';
import { AppNavBar } from './Modules/Views/AppNavBar';
import AppForm from './Modules/Views/AppForm';
import RFTextField from './Modules/Form/RFTextField';
import FormButton from './Modules/Form/FormButton';
import FormFeedback from './Modules/Form/FormFeedback';
import withRoot from './Modules/withRoot';

function ResetpasswordPage() {
  const [token, setToken] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    setToken(tokenFromUrl);
  }, []);

  const validate = (values: { [key: string]: string }) => {
    const errors: { [key: string]: string } = {};
    if (!values.password) errors.password = 'Password is required';
    if (!values.passwordConfirm) errors.passwordConfirm = 'Confirmation is required';
    if (values.password && values.passwordConfirm && values.password !== values.passwordConfirm) {
      errors.passwordConfirm = 'Passwords do not match';
    }
    return errors;
  };

  const handleSubmit = async (values: { password: string; passwordConfirm: string }) => {
    setSubmitError('');
    setSubmitSuccess('');
    setLoading(true);

    if (!token) {
      setSubmitError('Invalid or missing token.');
      setLoading(false);
      return;
    }

    interface ResetPasswordResponse {
        message?: string;
      }
      
      try {
        const response = await axios.post<ResetPasswordResponse>(
            'https://demo.insollex.com/api/reset-password', // ✅ Correct endpoint
            {
              token,
              password: values.password,
              password_confirm: values.passwordConfirm,
            }
          );
      
        setSubmitSuccess(response.data?.message || 'Password reset successfully.');
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } catch (err: any) {
        const message = err?.response?.data?.message || 'Failed to reset password. Please try again.';
        setSubmitError(message);
      } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <AppNavBar />
      <AppForm>
        <Typography variant="h3" gutterBottom marked="center" align="center">
          Reset Password
        </Typography>
        <Typography variant="body2" align="center">
          Enter your new password below.
        </Typography>

        {!token ? (
          <FormFeedback error sx={{ mt: 3 }}>
            Invalid reset link. Token is missing.
          </FormFeedback>
        ) : (
          <Form onSubmit={handleSubmit} validate={validate} subscription={{ submitting: true }}>
            {({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} noValidate style={{ marginTop: '2rem' }}>
                <Field
                  autoFocus
                  fullWidth
                  component={RFTextField}
                  disabled={submitting || loading}
                  label="New Password"
                  name="password"
                  type="password"
                  required
                />

                <Field
                  fullWidth
                  component={RFTextField}
                  disabled={submitting || loading}
                  label="Confirm New Password"
                  name="passwordConfirm"
                  type="password"
                  required
                />

                {submitError && (
                  <FormFeedback error sx={{ mt: 2 }}>
                    {submitError}
                  </FormFeedback>
                )}

                {submitSuccess && (
                  <FormFeedback sx={{ mt: 2 }}>
                    {submitSuccess}
                  </FormFeedback>
                )}

                <FormButton
                  sx={{ mt: 3, mb: 2 }}
                  disabled={submitting || loading}
                  size="large"
                  color="secondary"
                  fullWidth
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </FormButton>
              </form>
            )}
          </Form>
        )}
      </AppForm>
      <AppFooter />
    </React.Fragment>
  );
}

export default withRoot(ResetpasswordPage);
